/**
 * Web Audio playback engine for TTS voice clips.
 *
 * We fetch the mp3, decode it to an AudioBuffer, and play it through an
 * AudioBufferSourceNode — the same Web Audio path as `lesson/sounds.ts`.
 *
 * iOS SILENT SWITCH: this file was written (2026-06) on the belief that
 * Web Audio ignores the iPhone ring/silent switch. It is the reverse — the
 * switch mutes Web Audio and spares <audio> elements — so with the switch on,
 * lessons were silent (confirmed on three iPhones, 2026-10-08). The fix
 * lives in `./silentSwitch.ts`, installed at startup in main.tsx; this
 * engine needs nothing extra.
 *
 * Requirements this relies on:
 *   - CORS on the audio origin: decodeAudioData reads the bytes via fetch(),
 *     so the response must be CORS-readable. R2 (audio.fluent.ge) sends
 *     `access-control-allow-origin` for app.fluent.ge; dev is same-origin
 *     (`/audio`). An <audio> element wouldn't need this — fetch() does.
 *   - A user gesture to unlock the AudioContext. iOS starts the context
 *     'suspended'; we resume it on every pointer/touch/key event (see the
 *     listeners installed at the bottom) and again inside playClip.
 *
 * iOS INTERRUPTIONS: a phone call, Siri, another app's sound, or leaving
 * the app puts the context into WebKit's own 'interrupted' state — and it
 * often never comes back by itself (WebKit bug 261554 and others). A new
 * learner's report (2026-10-08, lesson 2) showed exactly that: 29 clips
 * played, then the context sat 'interrupted' and every later clip "played"
 * in silence. So: the gesture listeners never detach; returning to the
 * page tries resume(); and a tap that finds the context still interrupted
 * swaps in a fresh one (decoded buffers carry over — they are not tied to
 * a context).
 *
 * Public surface:
 *   - audioContext()          the shared context (lesson/sounds.ts uses it too)
 *   - unlockAudio()           wake the context (safe to call repeatedly)
 *   - preloadClip(key)        warm the buffer cache (fire-and-forget)
 *   - playClip(key, handlers) play a clip; returns a handle with stop()
 *   - loadClip(key)           fetch+decode → cached AudioBuffer
 *   - __resetEngineForTests() clear module state between vitest runs
 *
 * Single-clip model: only one clip plays at a time. Starting a new clip
 * supersedes the previous one (its `onEnded` does NOT fire — the new caller
 * owns playback now). Natural completion fires `onEnded`; decode/network
 * failure fires `onError`.
 */
import { audioUrl } from './audioUrl.js';

type WebkitWindow = Window & { webkitAudioContext?: typeof AudioContext };

let ctx: AudioContext | null = null;
const bufferCache = new Map<string, AudioBuffer>();
const inFlight = new Map<string, Promise<AudioBuffer>>();

/** Monotonic token — every playClip() bumps it so a superseded clip's
 *  async continuation and `ended` handler can detect they're stale. */
let playToken = 0;
let currentSource: AudioBufferSourceNode | null = null;
/** onEnded of the live clip — fired if its context is swapped out under it,
 *  so a speaker button or dialogue does not wait forever on a frozen clip. */
let currentOnEnded: (() => void) | null = null;

/** For in-app reports: did any clip actually start, and what failed last. */
let clipsStarted = 0;
let lastError: string | null = null;
let contextsReplaced = 0;

/** The one AudioContext for all app sound — voice clips here, effects in
 *  lesson/sounds.ts. Created on first use. */
export function audioContext(): AudioContext | null {
  return getCtx();
}

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  const Ctor = window.AudioContext ?? (window as WebkitWindow).webkitAudioContext;
  if (!Ctor) return null;
  try {
    ctx = new Ctor();
  } catch {
    return null;
  }
  return ctx;
}

/** Alive but silent. 'interrupted' is WebKit-only, so it is not in the
 *  AudioContextState type. */
function isAsleep(ac: AudioContext): boolean {
  const state = String(ac.state);
  return state === 'suspended' || state === 'interrupted';
}

/** Close the stuck context and start a fresh one. Must run inside a user
 *  gesture so iOS lets the new context start. */
function replaceCtx(): AudioContext | null {
  const old = ctx;
  ctx = null;
  const fresh = getCtx();
  if (!fresh) {
    ctx = old;
    return old;
  }
  contextsReplaced += 1;
  // A clip on the old context is frozen and will never end — drop it and
  // let its owner move on. Loads still in flight are left alone: they pick
  // up the fresh context when they finish (see loadClip / playClip).
  const src = currentSource;
  const ended = currentOnEnded;
  currentSource = null;
  currentOnEnded = null;
  if (src) {
    src.onended = null;
    try {
      src.stop();
    } catch {
      /* already stopped */
    }
  }
  old?.close?.().catch?.(() => {});
  ended?.();
  return fresh;
}

/** Wake the AudioContext. iOS requires a user gesture at least once;
 *  harmless to call when already running. `fromGesture` (only the window
 *  listeners below pass it) allows replacing a context iOS left
 *  'interrupted'. */
export function unlockAudio(fromGesture = false): void {
  let ac = getCtx();
  if (!ac) return;
  if (fromGesture && String(ac.state) === 'interrupted') {
    ac = replaceCtx();
    if (!ac) return;
  }
  if (isAsleep(ac)) {
    ac.resume().catch(() => {});
  }
}

/** Promise-wrapped decodeAudioData — Safari historically only had the
 *  callback form. ArrayBuffer is detached by decode, so callers must not
 *  reuse it. */
function decode(ac: AudioContext, data: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise((resolve, reject) => {
    // Modern browsers return a promise; older Safari uses callbacks. Cover both.
    const maybe = ac.decodeAudioData(data, resolve, reject) as unknown;
    if (maybe && typeof (maybe as Promise<AudioBuffer>).then === 'function') {
      (maybe as Promise<AudioBuffer>).then(resolve, reject);
    }
  });
}

/** Fetch + decode an audio_key into an AudioBuffer, cached. `reload` skips
 *  the HTTP cache (used by retry after a corrupt/failed download). */
export function loadClip(audioKey: string, reload = false): Promise<AudioBuffer> {
  const ac = getCtx();
  if (!ac) return Promise.reject(new Error('no-audio-context'));

  if (!reload) {
    const cached = bufferCache.get(audioKey);
    if (cached) return Promise.resolve(cached);
    const pending = inFlight.get(audioKey);
    if (pending) return pending;
  }

  const p = fetch(audioUrl(audioKey), reload ? { cache: 'reload' } : undefined)
    .then((res) => {
      if (!res.ok) throw new Error(`audio fetch ${res.status}`);
      return res.arrayBuffer();
    })
    // The context may have been replaced while downloading — decode on the live one.
    .then((buf) => decode(getCtx() ?? ac, buf))
    .then((decoded) => {
      bufferCache.set(audioKey, decoded);
      inFlight.delete(audioKey);
      return decoded;
    })
    .catch((err) => {
      inFlight.delete(audioKey);
      throw err;
    });

  if (!reload) inFlight.set(audioKey, p);
  return p;
}

/** Warm the buffer cache for an audio_key. Safe to call repeatedly. */
export function preloadClip(audioKey: string | undefined | null): void {
  if (!audioKey || typeof window === 'undefined') return;
  if (bufferCache.has(audioKey) || inFlight.has(audioKey)) return;
  loadClip(audioKey).catch(() => {
    /* preload is best-effort; a real play() will surface the error */
  });
}

export interface PlayHandlers {
  /** Fires when the clip finishes playing on its own. */
  onEnded?: () => void;
  /** Fires on network/decode/playback failure. */
  onError?: () => void;
  /** Force a fresh download (bypass HTTP + buffer cache). */
  reload?: boolean;
}

export interface PlayHandle {
  /** Stop this clip. Does not fire onEnded. */
  stop: () => void;
}

const NO_HANDLE: PlayHandle = { stop: () => {} };

/** Stop whatever is currently playing without notifying its owner. */
function stopCurrent(): void {
  playToken += 1; // supersede any in-flight load + the live source's ended
  const src = currentSource;
  currentSource = null;
  currentOnEnded = null;
  if (src) {
    src.onended = null;
    try {
      src.stop();
    } catch {
      /* already stopped */
    }
  }
}

/**
 * Play a clip through Web Audio. Stops any currently-playing clip first.
 * Returns a handle whose stop() halts playback silently.
 */
export function playClip(audioKey: string, handlers: PlayHandlers = {}): PlayHandle {
  const ac = getCtx();
  if (!ac) {
    lastError = 'no-audio-context';
    handlers.onError?.();
    return NO_HANDLE;
  }

  stopCurrent();
  unlockAudio();

  const myToken = playToken;
  let stopped = false;

  loadClip(audioKey, handlers.reload)
    .then((buffer) => {
      // Superseded by a newer play() or stopped before the buffer arrived.
      if (myToken !== playToken || stopped) return;
      // getCtx(), not `ac`: a tap may have replaced a stuck context meanwhile.
      const live = getCtx() ?? ac;
      const source = live.createBufferSource();
      source.buffer = buffer;
      source.connect(live.destination);
      source.onended = () => {
        if (myToken !== playToken) return; // superseded
        currentSource = null;
        currentOnEnded = null;
        handlers.onEnded?.();
      };
      currentSource = source;
      currentOnEnded = handlers.onEnded ?? null;
      try {
        source.start(0);
        clipsStarted += 1;
      } catch (err) {
        lastError = `start: ${errorText(err)}`;
        currentSource = null;
        currentOnEnded = null;
        if (myToken === playToken) handlers.onError?.();
      }
    })
    .catch((err: unknown) => {
      lastError = `load ${audioKey}: ${errorText(err)}`;
      if (myToken !== playToken || stopped) return;
      handlers.onError?.();
    });

  return {
    stop: () => {
      stopped = true;
      if (myToken !== playToken) return; // a newer clip already owns playback
      stopCurrent();
    },
  };
}

function errorText(err: unknown): string {
  if (err instanceof Error) return err.name === 'Error' ? err.message : `${err.name}: ${err.message}`;
  return String(err);
}

export interface AudioDiagnostics {
  /** AudioContext state: running / suspended / interrupted (iOS) / closed / none. */
  context: string;
  clips_started: number;
  last_error: string | null;
  /** Stuck ('interrupted') contexts swapped for a fresh one by a tap. */
  contexts_replaced: number;
}

/** Snapshot for in-app reports — read-only, never creates a context. */
export function audioDiagnostics(): AudioDiagnostics {
  return {
    context: ctx ? String(ctx.state) : 'none',
    clips_started: clipsStarted,
    last_error: lastError,
    contexts_replaced: contextsReplaced,
  };
}

/** Test-only — reset module state between vitest runs. */
export function __resetEngineForTests(): void {
  stopCurrent();
  bufferCache.clear();
  inFlight.clear();
  clipsStarted = 0;
  lastError = null;
  contextsReplaced = 0;
  if (ctx) {
    ctx.close?.().catch?.(() => {});
    ctx = null;
  }
}

/* Gesture listeners that wake the AudioContext. iOS needs resume() inside
 * a user gesture; after that, autoplay (e.g. the Dialogue auto-sequence)
 * works — until iOS suspends or interrupts the context again (call, lock,
 * app switch). So the listeners stay for the life of the page: each tap is
 * a cheap state check while the context is running. touchend/click are the
 * events iOS counts as a gesture for a touch; pointerdown covers mouse. */
if (typeof window !== 'undefined') {
  const onGesture = () => unlockAudio(true);
  for (const e of ['touchend', 'click', 'keydown'] as const) {
    window.addEventListener(e, onGesture, { passive: true });
  }
  // A touch's pointerdown is not a gesture to iOS — only a mouse's is.
  window.addEventListener(
    'pointerdown',
    (e) => unlockAudio(e.pointerType === 'mouse'),
    { passive: true },
  );
  // Coming back to the app: try to resume right away (works when iOS only
  // suspended the context); a stuck one is replaced on the next tap.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && ctx && isAsleep(ctx)) {
      ctx.resume().catch(() => {});
    }
  });
}
